import React, { useMemo } from "react";
import BaseChart from "../../components/ui/BaseChart";

const fullUsd = (n) =>
  `$${Number(n ?? 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

// `breakdown` comes from the KPI endpoint (date + client filtered): array of
// { clientId, name, color, amountUsd, count }, largest first.
const ClientDonutChart = ({ breakdown, mode = "expense" }) => {
  const isRevenue = mode === "revenue";

  const { points, total } = useMemo(() => {
    if (!breakdown?.length) return { points: [], total: 0 };
    const pts = breakdown
      .filter((c) => (c.amountUsd ?? 0) > 0)
      .map((c) => ({ name: c.name, color: c.color, y: c.amountUsd }));
    const sum = pts.reduce((a, p) => a + p.y, 0);
    return { points: pts, total: Math.round(sum * 100) / 100 };
  }, [breakdown]);

  const chartOptions = useMemo(() => {
    if (!points.length) return null;
    return {
      chart: {
        type: "pie",
        height: 340,
        backgroundColor: "transparent",
        style: { fontFamily: "Poppins, sans-serif" },
      },
      title: { text: null },
      tooltip: {
        backgroundColor: "#1e293b",
        style: { color: "#f8fafc", fontSize: "11px" },
        borderWidth: 0,
        borderRadius: 6,
        pointFormatter() {
          return `<span style="color:${this.color}">●</span> <b>${this.name}</b><br/>${fullUsd(this.y)} · <b>${this.percentage.toFixed(1)}%</b>`;
        },
      },
      plotOptions: {
        pie: {
          innerSize: "0%",
          borderWidth: 2,
          borderColor: "#ffffff",
          dataLabels: { enabled: false },
          showInLegend: true,
          states: { hover: { halo: { size: 6 } } },
        },
      },
      legend: {
        layout: "vertical",
        align: "right",
        verticalAlign: "middle",
        maxHeight: 280,
        navigation: { activeColor: "#4F46E5", inactiveColor: "#cbd5e1" },
        itemStyle: { fontSize: "10px", fontWeight: "normal", color: "#555" },
        itemHoverStyle: { color: "#111" },
        symbolRadius: 6,
        symbolHeight: 8,
        symbolWidth: 8,
      },
      series: [{ name: isRevenue ? "Revenue" : "Expense", data: points }],
    };
  }, [points, isRevenue]);

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <h4 className="text-sm font-semibold text-gray-700">
            {isRevenue ? "Revenue by Customer" : "Expense by Client"}
          </h4>
          <p className="text-[11px] text-gray-400 mt-0.5">Share of total · USD</p>
        </div>
        {total > 0 && (
          <span className="text-[11px] text-gray-400">
            Total <span className="font-semibold text-gray-700">{fullUsd(total)}</span>
          </span>
        )}
      </div>

      {chartOptions ? (
        <BaseChart options={chartOptions} className="w-full" />
      ) : (
        <p className="text-sm text-gray-400 py-20 text-center">No data for this period.</p>
      )}
    </div>
  );
};

export default ClientDonutChart;
